import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import {
  getAutoAssignSettings, toggleAutoAssign,
  updateAutoAssignStaffConfig, runAutoAssignPending,
  getAutoAssignRecentActivity,
  getAmazonAutoLockSetting, toggleAmazonAutoLock,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

export default function AutoAssignPage() {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === 'admin' || role === 'owner';

  const [enabled, setEnabled] = useState(false);
  const [amazonLockEnabled, setAmazonLockEnabled] = useState(true);
  const [amazonLockUpdatedBy, setAmazonLockUpdatedBy] = useState('');
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [edits, setEdits] = useState({}); // { staff_id: { priority, quota } }
  const [activity, setActivity] = useState([]);
  const [activitySummary, setActivitySummary] = useState({ total: 0, auto_assigned: 0, manual_assigned: 0, pending: 0 });
  const [activityDays, setActivityDays] = useState(7);
  const [activityFilter, setActivityFilter] = useState('all'); // all | auto | manual | pending

  const fetchActivity = useCallback(async (days = 7) => {
    try {
      const { data } = await getAutoAssignRecentActivity(days, 300);
      setActivity(data.items || []);
      setActivitySummary(data.summary || { total: 0, auto_assigned: 0, manual_assigned: 0, pending: 0 });
    } catch (err) {
      console.error('Activity fetch failed', err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getAutoAssignSettings();
      setEnabled(!!data.enabled);
      setStaff(data.staff || []);
      setEdits({});
      try {
        const { data: amz } = await getAmazonAutoLockSetting();
        setAmazonLockEnabled(!!amz.enabled);
        setAmazonLockUpdatedBy(amz.updated_by || '');
      } catch (_err) { /* non-fatal */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (isAdmin) fetchActivity(activityDays); }, [fetchActivity, activityDays, isAdmin]);

  const handleToggle = async (val) => {
    try {
      await toggleAutoAssign(val);
      setEnabled(val);
      toast.success(val ? 'Auto-assign ENABLED' : 'Auto-assign DISABLED');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    }
  };

  const handleAmazonLockToggle = async (val) => {
    try {
      await toggleAmazonAutoLock(val);
      setAmazonLockEnabled(val);
      toast.success(val
        ? 'Amazon auto-lock ENABLED — accounts ab assignment par lock honge'
        : 'Amazon auto-lock DISABLED — koi account assign nahi hoga');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    }
  };

  const setEdit = (sid, field, value) => {
    setEdits(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [field]: value } }));
  };

  const saveStaff = async (sid) => {
    const e = edits[sid] || {};
    const payload = {};
    if (e.priority !== undefined) payload.auto_assign_priority = parseInt(e.priority, 10) || 999;
    if (e.quota !== undefined) payload.daily_booking_quota = parseInt(e.quota, 10) || 0;
    if (Object.keys(payload).length === 0) { toast.info('Koi change nahi kiya'); return; }
    try {
      await updateAutoAssignStaffConfig(sid, payload);
      toast.success('Staff config updated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const toggleLeave = async (sid, current) => {
    try {
      await updateAutoAssignStaffConfig(sid, { is_on_leave: !current });
      toast.success(current ? 'Available now' : 'Marked on leave');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const runPending = async () => {
    setRunning(true);
    try {
      const { data } = await runAutoAssignPending();
      toast.success(`${data.assigned} files assigned, ${data.remaining_pending} still pending`);
      fetchData();
      fetchActivity(activityDays);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Run failed');
    } finally { setRunning(false); }
  };

  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="auto-assign-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Booking Files Auto-Assign</h1>
          <p className="text-sm text-gray-500 mt-1">Distribute uploaded booking files to staff by priority + daily quota. Files go to the highest-priority available staff whose today-load + file-bookings stays within their quota.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={runPending} disabled={!enabled || running} data-testid="run-pending-btn">
            {running ? 'Running…' : 'Run on Pending Files'}
          </Button>
        </div>
      </div>

      {/* Master toggle */}
      <div className="border rounded-md p-4 bg-white flex items-center justify-between">
        <div>
          <div className="font-semibold text-gray-900">Auto-Assign Engine</div>
          <div className="text-xs text-gray-500 mt-0.5">When ON, every newly uploaded file is automatically assigned. When OFF, files stay pending for manual assignment.</div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${enabled ? 'text-emerald-700' : 'text-gray-500'}`}>
            {enabled ? 'ENABLED' : 'DISABLED'}
          </span>
          <Switch checked={enabled} onCheckedChange={handleToggle} data-testid="auto-assign-toggle" />
        </div>
      </div>

      {/* Amazon Account Auto-Lock toggle */}
      <div className={`border rounded-md p-4 flex items-center justify-between ${amazonLockEnabled ? 'bg-white' : 'bg-amber-50 border-amber-300'}`}>
        <div>
          <div className="font-semibold text-gray-900 flex items-center gap-2">
            Amazon Account Auto-Lock
            {!amazonLockEnabled && (
              <span className="text-[10px] uppercase tracking-wide bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">Paused</span>
            )}
          </div>
          <div className="text-xs text-gray-600 mt-0.5 max-w-2xl">
            When <b>ON</b>: file assignment par staff ko Amazon accounts auto-lock ho jate hain (booking count proportional).
            When <b>OFF</b>: koi account assign nahi hoga — Amazon kaam paused hai to ye OFF rakho. Jab kaam start ho, ON kar do, naye files se accounts assign honge.
            {amazonLockUpdatedBy && (
              <span className="block mt-1 text-[11px] text-gray-500">Last changed by: {amazonLockUpdatedBy}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${amazonLockEnabled ? 'text-emerald-700' : 'text-amber-700'}`}>
            {amazonLockEnabled ? 'ENABLED' : 'DISABLED'}
          </span>
          <Switch checked={amazonLockEnabled} onCheckedChange={handleAmazonLockToggle} data-testid="amazon-auto-lock-toggle" />
        </div>
      </div>

      {/* Staff config table */}
      <div className="border rounded-md overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-12 text-center">#</TableHead>
              <TableHead>Staff</TableHead>
              <TableHead className="text-center">Priority<div className="text-[10px] text-gray-500 font-normal">(1 = first)</div></TableHead>
              <TableHead className="text-center">Daily Quota<div className="text-[10px] text-gray-500 font-normal">(0 = unlimited)</div></TableHead>
              <TableHead className="text-center">Today Load</TableHead>
              <TableHead className="text-center">On Leave</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-6">Loading…</TableCell></TableRow>
            ) : staff.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-6">No staff found</TableCell></TableRow>
            ) : staff.map((s, idx) => {
              const e = edits[s.id] || {};
              const overQuota = s.daily_booking_quota > 0 && s.today_load >= s.daily_booking_quota;
              return (
                <TableRow key={s.id} data-testid={`auto-staff-row-${s.id}`} className={s.is_on_leave ? 'bg-gray-100 opacity-70' : ''}>
                  <TableCell className="text-center font-medium">{idx + 1}</TableCell>
                  <TableCell>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-[11px] text-gray-500">{s.email}</div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Input
                      type="number"
                      min="1"
                      max="999"
                      defaultValue={s.auto_assign_priority}
                      onChange={(ev) => setEdit(s.id, 'priority', ev.target.value)}
                      className="h-8 w-20 text-center mx-auto"
                      data-testid={`priority-${s.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Input
                      type="number"
                      min="0"
                      defaultValue={s.daily_booking_quota}
                      onChange={(ev) => setEdit(s.id, 'quota', ev.target.value)}
                      className="h-8 w-28 text-center mx-auto"
                      data-testid={`quota-${s.id}`}
                    />
                  </TableCell>
                  <TableCell className={`text-center font-semibold ${overQuota ? 'text-red-600' : 'text-gray-700'}`}>
                    {s.today_load}
                    {s.daily_booking_quota > 0 && (
                      <span className="text-xs text-gray-400"> / {s.daily_booking_quota}</span>
                    )}
                    {overQuota && <div className="text-[10px] text-red-600 font-medium">⚠️ Over quota</div>}
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={s.is_on_leave}
                      onCheckedChange={() => toggleLeave(s.id, s.is_on_leave)}
                      data-testid={`leave-${s.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => saveStaff(s.id)} disabled={!e.priority && !e.quota} data-testid={`save-${s.id}`}>
                      Save
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="text-xs text-gray-500 leading-relaxed bg-blue-50 p-3 rounded">
        <b>Logic:</b> Files first go to staff with priority <b>1</b>. Once their today-load + file-bookings would exceed quota, the next priority gets the file. Staff on leave are skipped automatically until you turn off the leave toggle.<br/>
        <b>Tip:</b> Quota change anytime — for example, Ashish 1000 → 1500 — turant effect.
      </div>

      {/* Recent Activity — kis-ko kaun-sa file gaya */}
      <div className="border rounded-md bg-white overflow-hidden" data-testid="recent-activity-section">
        <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-semibold text-gray-900">Recent File Activity</div>
            <div className="text-xs text-gray-500 mt-0.5">Kaun-kaun se files aaye aur kis-ko assign hue (auto vs manual). Last {activityDays} days.</div>
          </div>
          <div className="flex items-center gap-2">
            <select
              className="text-xs border rounded px-2 py-1 h-8 bg-white"
              value={activityDays}
              onChange={(e) => setActivityDays(parseInt(e.target.value, 10))}
              data-testid="activity-days-select"
            >
              <option value={1}>Today</option>
              <option value={3}>Last 3 days</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <Button size="sm" variant="outline" onClick={() => fetchActivity(activityDays)} data-testid="refresh-activity-btn">
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary chips + filter */}
        <div className="px-4 py-3 flex flex-wrap gap-2 border-b bg-white">
          {[
            { key: 'all', label: 'All', count: activitySummary.total, color: 'bg-gray-100 text-gray-800 border-gray-300' },
            { key: 'auto', label: 'Auto-Assigned', count: activitySummary.auto_assigned, color: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
            { key: 'manual', label: 'Manual', count: activitySummary.manual_assigned, color: 'bg-blue-50 text-blue-700 border-blue-300' },
            { key: 'pending', label: 'Pending', count: activitySummary.pending, color: 'bg-amber-50 text-amber-700 border-amber-300' },
          ].map(chip => (
            <button
              key={chip.key}
              onClick={() => setActivityFilter(chip.key)}
              className={`text-xs px-3 py-1 rounded-full border font-medium ${chip.color} ${activityFilter === chip.key ? 'ring-2 ring-offset-1 ring-blue-400' : 'opacity-80 hover:opacity-100'}`}
              data-testid={`activity-filter-${chip.key}`}
            >
              {chip.label} <span className="ml-1 font-bold">{chip.count}</span>
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Date / Time</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Distributor</TableHead>
                <TableHead>Oil Co.</TableHead>
                <TableHead>Bookings</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const filtered = activity.filter(it => {
                  if (activityFilter === 'all') return true;
                  if (activityFilter === 'pending') return it.status === 'pending' || !it.assigned_to_id;
                  if (activityFilter === 'auto') return it.auto_assigned && it.assigned_to_id;
                  if (activityFilter === 'manual') return !it.auto_assigned && it.assigned_to_id;
                  return true;
                });
                if (filtered.length === 0) {
                  return <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-6">Koi files nahi mile is filter pe</TableCell></TableRow>;
                }
                return filtered.map(it => {
                  const isPending = it.status === 'pending' || !it.assigned_to_id;
                  const dt = it.assigned_at || it.created_at;
                  const fmt = dt ? new Date(dt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';
                  return (
                    <TableRow key={it.id} data-testid={`activity-row-${it.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmt}</TableCell>
                      <TableCell className="text-xs max-w-[260px] truncate" title={it.original_filename}>{it.original_filename}</TableCell>
                      <TableCell className="text-xs">
                        {it.distributor_name}
                        {it.distributor_agencies?.length > 0 && (
                          <div className="text-[10px] text-emerald-700 font-bold mt-0.5">
                            💰 {it.distributor_agencies.map(a => `${a.name.slice(0, 14)} ₹${a.rate}`).join(' · ')}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs uppercase">{it.oil_company}</TableCell>
                      <TableCell className="text-xs text-center">{it.total_bookings_count || '-'}</TableCell>
                      <TableCell className="text-xs">
                        {isPending ? (
                          <span className="text-amber-700 font-medium">— Pending —</span>
                        ) : (
                          <span className="font-medium text-gray-900">{it.assigned_to_name}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isPending ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-300">—</span>
                        ) : it.auto_assigned ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-300 font-medium">AUTO</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-300 font-medium">MANUAL</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${
                          it.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' :
                          it.status === 'assigned' ? 'bg-blue-50 text-blue-700 border-blue-300' :
                          it.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-300' :
                          'bg-gray-100 text-gray-700 border-gray-300'
                        }`}>{it.status?.toUpperCase()}</span>
                      </TableCell>
                    </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
